import { useEffect, useMemo, useRef, useState } from "react";
import { Image, Platform } from "react-native";
import { useQuery } from "@tanstack/react-query";
import {
  getJellyflixTrickplayManifest,
  JellyfinItem,
  jellyflixTrickplaySource,
  startJellyflixTrickplayGeneration,
} from "../api/jellyfin";
import { productProfile } from "../productProfile";

type MediaSource = NonNullable<JellyfinItem["MediaSources"]>[number];

export function usePlayerTrickplay({
  item,
  sources,
  selectedSourceId,
  serverUrl,
  playing,
}: {
  item?: JellyfinItem;
  sources: MediaSource[];
  selectedSourceId?: string;
  serverUrl?: string;
  playing: boolean;
}) {
  const itemId = item?.Id ?? "";
  const startedItemRef = useRef("");
  const [preloadedUrls, setPreloadedUrls] = useState<ReadonlySet<string>>(() => new Set());
  const manifestQuery = useQuery({
    queryKey: ["player", "jellyflix-trickplay", itemId],
    queryFn: () => getJellyflixTrickplayManifest(itemId),
    enabled: Boolean(itemId) && productProfile.privateBackendEnabled,
    retry: 1,
    staleTime: 15_000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "ready" || status === "failed" || status === "cancelled"
        ? false
        : 2_500;
    },
  });

  const sheetUrls = useMemo(() => {
    const sheets = manifestQuery.data?.sheets ?? [];
    return [...sheets]
      .sort((left, right) => left.startMs - right.startMs || left.index - right.index)
      .map((sheet) => sheet.url)
      .filter((url, index, urls) => Boolean(url) && urls.indexOf(url) === index);
  }, [manifestQuery.data?.sheets]);

  useEffect(() => {
    let cancelled = false;
    const ready = new Set<string>();
    setPreloadedUrls(ready);
    if (!sheetUrls.length) return () => { cancelled = true; };

    let nextIndex = 0;
    const concurrency = Platform.isTV ? 1 : Platform.OS === "web" ? 6 : 2;
    const worker = async () => {
      while (!cancelled && nextIndex < sheetUrls.length) {
        const url = sheetUrls[nextIndex++];
        try {
          const loaded = await Image.prefetch(jellyflixTrickplaySource(url).uri);
          if (!cancelled && loaded !== false) {
            ready.add(url);
            setPreloadedUrls(new Set(ready));
          }
        } catch {
          // A failed sheet stays unavailable; legacy trickplay/time remains usable.
        }
      }
    };
    void Promise.all(Array.from({ length: Math.min(concurrency, sheetUrls.length) }, worker));
    return () => { cancelled = true; };
  }, [itemId, sheetUrls]);

  useEffect(() => {
    if (!productProfile.privateBackendEnabled || !playing || !itemId || !selectedSourceId || !serverUrl) return;
    const status = manifestQuery.data?.status;
    if (status && status !== "missing" && status !== "failed" && status !== "cancelled") return;
    if (startedItemRef.current === itemId) return;
    startedItemRef.current = itemId;
    void startJellyflixTrickplayGeneration(itemId, {
      itemId,
      itemName: item?.Name,
      runtimeTicks: item?.RunTimeTicks,
      seriesId: item?.SeriesId,
      seasonId: item?.ParentId,
      episodeIndex: item?.IndexNumber,
      serverUrl,
      selectedMediaSourceId: selectedSourceId,
      mediaSources: sources.map((source) => ({
        Id: source.Id,
        Name: source.Name,
        Container: source.Container,
        RunTimeTicks: source.RunTimeTicks,
        SupportsDirectPlay: source.SupportsDirectPlay,
        SupportsDirectStream: source.SupportsDirectStream,
        SupportsTranscoding: source.SupportsTranscoding,
        ContentHash: source.ContentHash,
        ChapterSourceContentHash: source.ChapterSourceContentHash,
        VirtualChapters: source.VirtualChapters,
        ChaptersInherited: source.ChaptersInherited,
        Chapters: source.Chapters,
        MediaStreams: source.MediaStreams,
      })),
    }).then(() => manifestQuery.refetch()).catch(() => undefined);
  }, [item, itemId, manifestQuery.data?.status, playing, selectedSourceId, serverUrl, sources]);

  return { ...manifestQuery, preloadedUrls };
}
